import { walkTree, type Config, type Data, type Fields } from '@puckeditor/core'
import { ButtonBlock } from './components/ButtonBlock'
import { ColumnsBlock } from './components/Columns'
import { HeadingBlock } from './components/Heading'
import { ParagraphBlock } from './components/Paragraph'
import { ImageBlock } from './components/ImageBlock'
import { SpacerBlock } from './components/Spacer'
import { DividerBlock } from './components/Divider'
import { ContainerBlock } from './components/Container'
import { VideoEmbedBlock } from './components/VideoEmbed'
import { BadgeBlock } from './components/Badge'
import ColorField from './components/ColorField'
import PaddingField, { defaultPadding, paddingToStyle } from './components/PaddingField'
import { plainTextToRichText } from './richText'

const positionOptions = [
    { label: 'Center', value: 'center' },
    { label: 'Bottom Right', value: 'bottom_right' },
    { label: 'Bottom Left', value: 'bottom_left' },
    { label: 'Top Bar', value: 'top_bar' },
    { label: 'Full Screen', value: 'full_screen' },
]

const animationOptions = [
    { label: 'None', value: 'none' },
    { label: 'Fade', value: 'fade' },
    { label: 'Slide Up', value: 'slide_up' },
    { label: 'Scale', value: 'scale' },
]

export const puckConfig: Config = {
    categories: {
        content: {
            title: 'Content',
            components: ['Heading', 'Paragraph', 'Badge'],
            defaultExpanded: true,
        },
        media: {
            title: 'Media',
            components: ['Image', 'Video'],
        },
        actions: {
            title: 'Actions',
            components: ['Button'],
        },
        layout: {
            title: 'Layout',
            components: ['Columns', 'Container', 'Spacer', 'Divider'],
        },
    },
    root: {
        defaultProps: {
            backgroundColor: '#FFFFFF',
            padding: defaultPadding,
            borderRadius: 12,
            position: 'center',
            animation: 'fade',
            overlay: true,
            maxWidth: 480,
        },
        fields: {
            position: {
                type: 'select',
                label: 'Position',
                options: positionOptions,
            },
            animation: {
                type: 'select',
                label: 'Animation',
                options: animationOptions,
            },
            overlay: {
                type: 'radio',
                label: 'Show Overlay',
                options: [
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                ],
            },
            maxWidth: {
                type: 'number',
                label: 'Max Width (px)',
                min: 200,
                max: 800,
            },
            borderRadius: {
                type: 'number',
                label: 'Border Radius',
                min: 0,
                max: 48,
            },
            backgroundColor: {
                type: 'custom',
                render: ({ value, onChange }) => <ColorField label="Background Color" value={value} onChange={onChange} allowTransparent />,
            },
            padding: {
                type: 'custom',
                render: ({ value, onChange }) => <PaddingField value={value} onChange={onChange} />,
            },
        },
        render: ({ children, backgroundColor, padding, borderRadius, position }: any) => {
            const isFS = position === 'full_screen'
            return (
                <div style={{
                    backgroundColor,
                    padding: paddingToStyle(padding),
                    borderRadius: isFS ? 0 : borderRadius,
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    ...(isFS ? { height: '100%', display: 'flex', flexDirection: 'column' as const } : {}),
                }}>
                    {children}
                </div>
            )
        },
    },
    components: {
        Heading: HeadingBlock,
        Paragraph: ParagraphBlock,
        Badge: BadgeBlock,
        Image: ImageBlock,
        Video: VideoEmbedBlock,
        Button: ButtonBlock,
        Columns: ColumnsBlock,
        Container: ContainerBlock,
        Spacer: SpacerBlock,
        Divider: DividerBlock,
    },
}

const richTextKeys = (fields: Fields = {}) => Object.keys(fields).filter(key => fields[key].type === 'richtext')

export const exportConfig: Config = {
    ...puckConfig,
    components: Object.fromEntries(Object.entries(puckConfig.components).map(([name, component]) => [name, {
        ...component,
        fields: Object.fromEntries(Object.entries(component.fields ?? {}).map(([key, field]) => [
            key,
            field.type === 'richtext' ? { type: 'textarea' } : field,
        ])),
    }])),
}

export const migrateRichText = (data: Data): Data => {
    const rootProps = (data.root as { props?: Record<string, unknown> }).props ?? {}
    if (rootProps.richText) return data

    const migrated = walkTree(data, puckConfig, content => content.map(item => {
        const props = { ...item.props }
        for (const key of richTextKeys(puckConfig.components[item.type]?.fields)) {
            if (typeof props[key] === 'string') props[key] = plainTextToRichText(props[key])
        }
        return { ...item, props }
    }))
    return { ...migrated, root: { ...migrated.root, props: { ...rootProps, richText: true } } as Data['root'] }
}
